import { Div, DivProps, Heading, P } from '@kizi/core-components';
import {
  Anchor,
  capitalise,
  IconV2,
  Table,
  withTooltip,
} from '@kizi/shared-components';
import * as React from 'react';
import { Consumption, ProductProps } from '.';
import { formatPrice } from '../utils/global';
import { EnergyProduct } from '../utils/product';
import { Field } from './Product';
import { ListComp, ListGroup } from './Product/ExpandedPart/List';
import { PriceCapCeillingLabel } from './Product/ExpandedPart/Rates';

const measure = {
  gas: 'm³',
  electricity: 'kWh',
};

const borderBottom = {
  borderBottom: '1px solid',
  borderBottomColor: 'second.1',
};
const borderTop = {
  borderTop: '1px solid',
  borderTopColor: 'second.1',
};

function replaceUsageLabel(text: string, type: ENERGY_TYPE) {
  const consumptionLabel =
    type === 'electricity' ? 'Stroomverbruik' : 'Gasverbruik';

  return text
    .replace('Leveringskosten', consumptionLabel)
    .replace('Terugleververgoeding', consumptionLabel)
    .replace('Terugleververgoeding', consumptionLabel)
    .replace('Duurzame Energie', 'duurzame energie')
    .replace('normaaltarief', 'normaal')
    .replace('daltarief', 'dal');
}

const fixedDeliveryCostsKey = 'Vaste leveringskosten';
const fixedReturnCostsKey = 'Vaste terugleveringskosten';
const reductionEnergyTaxKey = 'Vermindering energiebelasting';

function getBreakdown(data: EnergyProduct, type: ENERGY_TYPE) {
  const { price } = data;
  const { breakdown } = price;
  const { breakdown: bb } = breakdown;

  if (Object.keys(bb).includes(type)) {
    return bb[type];
  } else return {};
}
const IconWithTooltip = withTooltip(IconV2);

const cbFixedReturnCosts = (
  <>
    <P variant="footnoteRegular">
      Met vaste terugleveringskosten worden de kosten gedekt voor het
      terugleveren van je opgewekte stroom. De kosten bestaan uit een vast
      bedrag per dag waarvan de hoogte afhankelijk is van de hoeveelheid
      daadwerkelijk opgewekte stroom per jaar. Per schaal gelden er vaste
      terugleveringskosten. Let op: de kosten die je hier ziet zijn gebaseerd op
      een geschatte hoeveelheid opgewekte stroom. De definitieve kosten worden
      berekend op je jaar- of eindnota.
    </P>
    <P variant="footnoteRegular" mt={2}>
      De vaste terugleveringskosten gelden alleen als je zelf stroom opwekt en
      zullen van toepassing zijn zodra we teruglevering van elektriciteit op je
      aansluiting registreren. Ook als dit tijdens de looptijd van je contract
      is. Voor meer info over de schalen en bijbehorende kosten zie&nbsp;
      <Anchor
        style={{
          textDecoration: 'none',
          wordBreak: 'break-all',
        }}
        href={'https://www.consumentenbond.nl/zonnepanelen/terugleverkosten'}
        type={'website'}
        color={'main.1'}
      >
        Zonnepanelen | Terugleverkosten | Consumentenbond
      </Anchor>
    </P>
  </>
);

function getDeliveryRows(data: EnergyProduct, type: ENERGY_TYPE) {
  const obj = getBreakdown(data, type);

  if (!Object.keys(obj).length) return [];

  const { delivery, transport } = obj;

  const { rows } = delivery['items']['deliveryTotal'];
  const showScale =
    rows.filter((row) => row.delivery === 'Energiebelasting').length > 1;

  const fixedDeliveryCostsObject = rows.find(
    (row) => row.delivery === fixedDeliveryCostsKey,
  );
  const fixedReturnCostsObject = rows.find(
    (row) => row.delivery === fixedReturnCostsKey,
  );

  const reductionEnergyTaxObject = rows.find(
    (row) => row.delivery === reductionEnergyTaxKey,
  );

  let deliveryRows = [
    ['', 'Verbruik', 'Tarief', 'Per jaar'],
    ...rows
      .filter(
        (row) =>
          ![
            fixedDeliveryCostsKey,
            fixedReturnCostsKey,
            reductionEnergyTaxKey,
          ].includes(row.delivery),
      )
      .map((row, i) => {
        const { delivery: rowDelivery, scale, tariff, usage, priceYear } = row;
        const label = replaceUsageLabel(
          scale && showScale
            ? `${rowDelivery} (${scale} ${measure[type]})`
            : rowDelivery,
          type,
        );

        const newLabel = label.replace(/ \([\s\S]*?\)/g, ''); // Removes text inside parenthesis
        const textInsideParenthesis = label.match(/\(([^)]+)\)/)?.[1];

        return [
          <Div display="flex" alignItems="center" key={i}>
            {newLabel}
            {label.includes('boven') && (
              <PriceCapCeillingLabel
                mx={2}
                text={textInsideParenthesis}
                fontSize="12px"
              />
            )}
            {label.includes('onder') && (
              <PriceCapCeillingLabel
                mx={2}
                text={textInsideParenthesis}
                fontSize="12px"
              />
            )}
          </Div>,
          usage ? `${usage} ${measure[type]}` : '',
          tariff ? formatPrice(tariff, 5) : '',
          formatPrice(priceYear),
        ];
      }),
  ];

  if (fixedDeliveryCostsObject) {
    deliveryRows.push([
      fixedDeliveryCostsObject.delivery,
      '',
      '',
      formatPrice(fixedDeliveryCostsObject.priceYear),
    ]);
  }
  if (fixedReturnCostsObject) {
    const { delivery, scale, usage, tariff, priceYear } =
      fixedReturnCostsObject;
    deliveryRows.push([
      <Div
        css={{
          minWidth: '260px',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
        key={delivery}
      >
        <P>{delivery}</P>
        &nbsp;
        {scale && <P variant="captionRegularSmall">({scale})</P>}
        <IconWithTooltip
          name="info"
          size={14}
          css={{
            fill: '#BEC0C3',
            marginRight: '0 !important',
            marginLeft: 1,
          }}
          _tooltip={cbFixedReturnCosts}
          cursor="pointer"
        />
      </Div>,
      usage ? `${usage} ${measure[type]}` : '',
      tariff ? formatPrice(tariff, 5) : '',
      formatPrice(priceYear),
    ]);
  }

  if (reductionEnergyTaxObject) {
    deliveryRows.push([
      reductionEnergyTaxObject.delivery,
      '',
      '',
      formatPrice(reductionEnergyTaxObject.priceYear),
    ]);
  }

  const transportTotal = transport['total'];

  deliveryRows = [
    ...deliveryRows,
    [returnNetbeheerkostenLabel(type), '', '', formatPrice(transportTotal)],
    [
      returnTotaalLabel(type),
      '',
      '',
      formatPrice(
        data.price.breakdown.priceSummary[`total${capitalise(type)}`],
      ),
    ],
  ];

  return deliveryRows;
}

function returnNetbeheerkostenLabel(type: string) {
  if (type === 'electricity') {
    return 'Netbeheerkosten stroom';
  }
  return 'Netbeheerkosten gas';
}

function returnTotaalLabel(type: string) {
  if (type === 'electricity') {
    return 'Totaal stroom';
  }
  return 'Totaal gas';
}

enum ENERGY_TYPE {
  ELECTRICITY = 'electricity',
  GAS = 'gas',
}

function getElectricityLabel(value?: number | string) {
  return value ? `${value} ${measure['electricity']}` : '';
}

function getUsageGroups(consumption: Consumption): ListGroup[][] {
  const {
    electricitysupplyhigh,
    electricitysupplylow,
    electricityusagehigh,
    electricityusagelow,
    gasusage,
  } = consumption;

  let usageGroups: ListGroup[][] = [];

  if (electricityusagehigh || electricityusagelow) {
    let usageList = [
      {
        label: 'Normaal',
        value: getElectricityLabel(electricityusagehigh),
      },
    ];

    if (electricityusagelow) {
      usageList.push({
        label: 'Dal',
        value: getElectricityLabel(electricityusagelow),
      });
    }

    usageGroups.push([
      {
        title: 'Stroomverbruik',
        list: usageList,
      },
    ]);

    let productionList: Field[] = [];

    if (electricitysupplyhigh) {
      productionList.push({
        label: 'Normaal',
        value: getElectricityLabel(electricitysupplyhigh),
      });
    }

    if (electricitysupplylow) {
      productionList.push({
        label: 'Dal',
        value: getElectricityLabel(electricitysupplylow),
      });
    }

    if (productionList.length > 0) {
      usageGroups[0] = [
        ...usageGroups[0],
        {
          title: 'Opgewekte stroom',
          list: productionList,
        },
      ];
    }
  }

  if (gasusage) {
    usageGroups.push([
      {
        title: 'Gasverbruik',
        list: [
          {
            label: 'Verbruik',
            value: `${gasusage} ${measure[ENERGY_TYPE.GAS]}`,
          },
        ],
      },
    ]);
  }

  return usageGroups;
}

export interface MobileRowProps extends DivProps {
  leftCell: React.ReactNode;
  rightCell: React.ReactNode;
  variant: string;
}

export const MobileRow: React.FC<MobileRowProps> = ({
  leftCell,
  rightCell,
  variant,
  ...divProps
}) => {
  return (
    <Div display="flex" justifyContent="space-between" {...divProps}>
      <P variant={variant}>{leftCell}</P>
      <Div>
        <P textAlign="left" variant={variant}>
          {rightCell}
        </P>
      </Div>
    </Div>
  );
};
export interface MobileTableProps extends DivProps {
  rows: React.ReactNode[][];
}

export const MobileTable: React.FC<MobileTableProps> = ({
  rows,
  ...restProps
}) => {
  const lastIndex = rows.length - 1;

  const tableHeadLabels = rows[0];
  const consumptionLabel = tableHeadLabels[1];
  const tariffLable = tableHeadLabels[2];

  const lastRow = rows[rows.length - 1];
  const totalPrice = lastRow[3];
  const totalPriceLabel = lastRow[0];

  const mainRows = rows.slice(1, lastIndex);

  return (
    <Div display="flex" flexDirection="column" {...restProps}>
      {mainRows.map((row, index) => {
        const hasConsumptionAndTariff = row[1] && row[2];

        return (
          <Div key={`${index}-energy-specific-tariff`} py={3} {...borderBottom}>
            <MobileRow
              data-testid={`energy-detail-row-${index}`}
              leftCell={row[0]}
              rightCell={row[3]}
              variant="baseBody"
            />
            {hasConsumptionAndTariff && (
              <Div ml={4} width="50%">
                <MobileRow
                  leftCell={consumptionLabel}
                  rightCell={row[1]}
                  variant="footnoteAdditionalInfo"
                  data-testid={`consumption-row-${index}`}
                />
                <MobileRow
                  leftCell={tariffLable}
                  rightCell={row[2]}
                  variant="footnoteAdditionalInfo"
                  data-testid={`tariff-row-${index}`}
                />
              </Div>
            )}
          </Div>
        );
      })}
      <MobileRow
        leftCell={totalPriceLabel}
        rightCell={totalPrice}
        py={3}
        variant="baseBodyImportant"
      />
    </Div>
  );
};

interface ProductDetailsTariffsProps
  extends Omit<ProductProps, 'data' | 'type' | 'nextIndex'> {
  data: EnergyProduct;
}

export const ProductDetailsTariffs: React.FC<ProductDetailsTariffsProps> =
  React.forwardRef<HTMLDivElement, ProductDetailsTariffsProps>(
    ({ data, priceMode, ...restProps }: ProductDetailsTariffsProps, ref) => {
      const {
        electricitysupplyhigh = 0,
        electricitysupplylow = 0,
        electricityusagehigh = '',
        electricityusagelow = '',
        gasusage = '',
        price,
      } = data;
      const { year } = price;
      const { discountIncluded } = year;

      const usageGroups: ListGroup[][] = getUsageGroups({
        electricitysupplyhigh,
        electricitysupplylow,
        electricityusagehigh,
        electricityusagelow,
        gasusage,
      });

      const showDiscount = discountIncluded > 0;

      const gasData = getDeliveryRows(data, ENERGY_TYPE.GAS);
      const electricityData = getDeliveryRows(data, ENERGY_TYPE.ELECTRICITY);
      const isGasProduct = gasData.length > 0;
      const isElectricityProduct = electricityData.length > 0;
      const isElGasProduct = isElectricityProduct && isGasProduct;

      const hasSolarPanels =
        electricitysupplyhigh > 0 || electricitysupplylow > 0;

      const yearlyPrice = Number(price.breakdown.priceSummary.total.toFixed(2));

      return (
        <Div {...restProps} ref={ref}>
          {hasSolarPanels && (
            <>
              <Heading mb={3} mt={0} as="h4" variant="H4">
                Teruglevering
              </Heading>
              <P mb={6} variant="bodyRegular">
                {
                  'Per meter wordt apart gesaldeerd. Houd je een overschot over op normaal of dal? Dan wordt dit verrekend tegen het teruglevertarief van die meter (normaal of dal).'
                }
              </P>
              <Div {...borderBottom} display={[null, 'flex']} pb={6} mb={6}>
                <ListComp
                  mr={6}
                  width="100%"
                  groups={usageGroups[0] || []}
                  listVariant="tariffsOverlayProductDetails"
                  listItemTitleVariant="baseBodyImportant"
                />
                <ListComp
                  ml={[null, null, 6]}
                  mt={[6, null, 0]}
                  width="100%"
                  groups={usageGroups[1] || []}
                  listVariant="tariffsOverlayProductDetails"
                  listItemTitleVariant="baseBodyImportant"
                />
              </Div>
            </>
          )}
          {isElectricityProduct && (
            <Div>
              <Heading m={0} as="h4" variant="H4">
                Stroom
              </Heading>
              <Table
                display={['none', null, 'flex']}
                {...borderBottom}
                rows={electricityData}
                variant="productModal"
              />
              <MobileTable
                display={['flex', null, 'none']}
                {...borderBottom}
                rows={electricityData}
              />
            </Div>
          )}
          {isGasProduct && (
            <Div>
              <Heading mb={0} mt={6} as="h4" variant="H4">
                Gas
              </Heading>
              <Table
                display={['none', null, 'flex']}
                {...borderBottom}
                rows={gasData}
                variant="productModal"
              />
              <MobileTable
                display={['flex', null, 'none']}
                {...borderBottom}
                rows={gasData}
              />
            </Div>
          )}
          {(isElGasProduct || showDiscount) && (
            <Div {...borderBottom} fontWeight={1} mb={3} py={6}>
              {isElectricityProduct && isGasProduct && (
                <Div
                  display="flex"
                  justifyContent="space-between"
                  pb={showDiscount ? 6 : 0}
                >
                  <P variant="baseBodyImportant">Totaal stroom en gas</P>
                  <P variant="baseBodyImportant">
                    {formatPrice(yearlyPrice + discountIncluded)}
                  </P>
                </Div>
              )}
              {showDiscount && (
                <Div
                  {...borderTop}
                  display="flex"
                  justifyContent="space-between"
                  pt={6}
                >
                  <P variant="baseBodyImportant">Loyaliteitsbonus per jaar</P>
                  <P variant="baseBodyImportant">
                    {formatPrice(-discountIncluded)}
                  </P>
                </Div>
              )}
            </Div>
          )}
          <Div
            display="flex"
            justifyContent="space-between"
            fontWeight={1}
            fontFamily="body"
          >
            <Div fontSize={[4, 6]} py={3}>
              Totaal per jaar
            </Div>
            <Div fontSize={[4, 6]} py={3} textAlign="right">
              {formatPrice(yearlyPrice)}
            </Div>
          </Div>
        </Div>
      );
    },
  );
